import { useState } from "react";
import { api, ApiError } from "@/lib/api-client";

interface UseZoneTemplateReturn {
  isLoading: boolean;
  error: string | null;
  fetchTemplate: () => Promise<Record<string, unknown> | null>;
  uploadTemplate: (file: File) => Promise<Record<string, unknown>>;
  deleteTemplate: () => Promise<boolean>;
  updateSettings: (strategy: string) => Promise<Record<string, unknown>>;
  updatePageAnchor: (pageNumber: number, anchorText: string) => Promise<Record<string, unknown>>;
}

/**
 * Zone-template admin hook. All network I/O flows through `api` so 401 responses
 * bounce to Suite SSO (the Skarn non-negotiable 401 handling contract).
 */
export function useZoneTemplate(jobTypeId: string, formId?: string): UseZoneTemplateReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = formId ? `?form=${encodeURIComponent(formId)}` : '';

  async function fetchTemplate(): Promise<Record<string, unknown> | null> {
    setIsLoading(true);
    setError(null);
    try {
      return await api.get<Record<string, unknown>>(`/job-types/${jobTypeId}/zone-template${q}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw err;
      if (err instanceof ApiError && err.status === 404) return null;
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  async function uploadTemplate(file: File): Promise<Record<string, unknown>> {
    setIsLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      return await api.upload<Record<string, unknown>>(
        `/job-types/${jobTypeId}/zone-template${q}`,
        formData,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw err;
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  async function deleteTemplate(): Promise<boolean> {
    setIsLoading(true);
    setError(null);
    try {
      await api.delete<void>(`/job-types/${jobTypeId}/zone-template${q}`);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw err;
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }

  async function updateSettings(strategy: string): Promise<Record<string, unknown>> {
    setIsLoading(true);
    setError(null);
    try {
      return await api.patch<Record<string, unknown>>(
        `/job-types/${jobTypeId}/zone-template/settings${q}`,
        { pageMatchingStrategy: strategy },
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw err;
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  async function updatePageAnchor(
    pageNumber: number,
    anchorText: string,
  ): Promise<Record<string, unknown>> {
    setIsLoading(true);
    setError(null);
    try {
      return await api.patch<Record<string, unknown>>(
        `/job-types/${jobTypeId}/zone-template/pages/${pageNumber}${q}`,
        { anchorText },
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) throw err;
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  return {
    isLoading,
    error,
    fetchTemplate,
    uploadTemplate,
    deleteTemplate,
    updateSettings,
    updatePageAnchor,
  };
}
